/**
 * QR Scan Landing Page
 *
 * Route: /assets/:id?via=qr
 *
 * Shown when a user scans a QR code with their phone.
 * Mobile-first, minimal info — just what you need to identify the asset.
 *
 * Three states:
 *   1. Normal   — shows asset card with basic info + holder
 *   2. Restricted soft — asset exists but details hidden (Viewer scanning restricted asset)
 *   3. Full detail link — for GA/Admin who can see more
 */

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Package,
  User,
  MapPin,
  Tag,
  Building2,
  AlertTriangle,
  ArrowRight,
  CheckCircle,
  Shield,
} from "lucide-react";
import { useAsset } from "../hooks/useAssets";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/asset/AssetHelpers.jsx";
import { formatDate } from "../utils/format";
import { Spinner } from "../components/ui/index.jsx";

const BACKEND_URL = import.meta.env.VITE_API_URL || "";
const CAN_SEE_FULL = ["ADMIN", "GA", "FINANCE", "EXECUTIVE"];

// ---------------------------------------------------------------------------
// Fetch public asset data (no auth header)
// ---------------------------------------------------------------------------
function usePublicAsset(id) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    fetch(`${BACKEND_URL}/public/assets/${id}/scan`)
      .then((r) => {
        if (r.status === 404) throw new Error("not_found");
        if (!r.ok) throw new Error("server_error");
        return r.json();
      })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  return { data, loading, error };
}

// ---------------------------------------------------------------------------
// Info row
// ---------------------------------------------------------------------------
function Row({ icon: Icon, label, value, mono }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0">
      <div className="mt-0.5 h-8 w-8 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">
          {label}
        </p>
        <p
          className={`text-sm text-slate-900 mt-0.5 ${mono ? "font-mono" : "font-medium"}`}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Warranty chip
// ---------------------------------------------------------------------------
function WarrantyChip({ warrantyStatus }) {
  if (!warrantyStatus) return null;
  const { status, daysRemaining } = warrantyStatus;
  const cfg = {
    ACTIVE: {
      label: `Warranty active · ${daysRemaining}d left`,
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
    },
    EXPIRING_SOON: {
      label: `Warranty expiring · ${daysRemaining}d left`,
      cls: "bg-amber-50 text-amber-700 border-amber-200",
    },
    EXPIRED: {
      label: "Warranty expired",
      cls: "bg-rose-50 text-rose-700 border-rose-200",
    },
  }[status];
  if (!cfg) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${cfg.cls}`}
    >
      <CheckCircle className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Status label
// ---------------------------------------------------------------------------
const STATUS_LABELS = {
  ACTIVE: "Active",
  IDLE: "Idle",
  DAMAGED: "Damaged",
  UNDER_REPAIR: "Under Repair",
  RETIRED: "Retired",
  LOST: "Lost",
};

const STATUS_COLORS = {
  ACTIVE: "bg-emerald-100 text-emerald-700",
  IDLE: "bg-slate-100 text-slate-600",
  DAMAGED: "bg-rose-100 text-rose-700",
  UNDER_REPAIR: "bg-amber-100 text-amber-700",
  RETIRED: "bg-slate-100 text-slate-400",
  LOST: "bg-rose-100 text-rose-700",
};

function StatusPill({ status }) {
  return (
    <span
      className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[status] || "bg-slate-100 text-slate-600"}`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function QrScanPage() {
  const { id } = useParams();
  const { user, isLoading: authLoading } = useAuth();
  const { data: asset, loading, error } = usePublicAsset(id);

  const canSeeFullDetail = user && CAN_SEE_FULL.includes(user.role);

  // Loading
  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  // Not found
  if (error === "not_found" || (!loading && !asset)) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="h-16 w-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4">
            <Package className="h-8 w-8 text-rose-400" />
          </div>
          <h1 className="text-lg font-bold text-slate-900">Asset Not Found</h1>
          <p className="text-sm text-slate-500 mt-2">
            This QR code doesn't match any active asset. The label may be
            damaged or the asset has been retired.
          </p>
          <p className="text-xs text-slate-300 mt-4 font-mono">{id}</p>
        </div>
      </div>
    );
  }

  // Server error
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-lg font-bold text-slate-900">
            Something went wrong
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Unable to load asset information. Please try again.
          </p>
        </div>
      </div>
    );
  }

  // Restricted asset
  if (asset.isRestricted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="h-16 w-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-amber-500" />
          </div>
          <h1 className="text-lg font-bold text-slate-900">Restricted Asset</h1>
          <p className="text-sm text-slate-500 mt-2">
            {asset.restrictedMessage}
          </p>
          <div className="mt-5 bg-white rounded-xl border border-slate-200 px-4 py-3 text-left">
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">
              Asset Number
            </p>
            <p className="font-mono text-sm font-bold text-slate-700 mt-0.5">
              {asset.assetNumber}
            </p>
          </div>
          {!user && (
            <Link
              to="/login"
              className="mt-4 inline-block text-sm text-primary-600 hover:underline"
            >
              Sign in for more access →
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-primary-600 flex items-center justify-center shrink-0">
          <Package className="h-4 w-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-400">Rhaya Inventory</p>
          <p className="text-sm font-semibold text-slate-900 truncate">
            Asset Information
          </p>
        </div>
        <StatusPill status={asset.status} />
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
        {/* Identity card */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          {/* Asset number banner */}
          <div className="bg-primary-600 px-4 py-3">
            <p className="text-primary-200 text-xs font-medium uppercase tracking-wide">
              Asset Number
            </p>
            <p className="text-white font-mono text-xl font-bold tracking-wide mt-0.5">
              {asset.assetNumber}
            </p>
          </div>

          {/* Core details */}
          <div className="px-4 py-2">
            <div className="py-3 border-b border-slate-100">
              <p className="text-base font-semibold text-slate-900">
                {asset.name}
              </p>
              {(asset.brand || asset.model) && (
                <p className="text-sm text-slate-500 mt-0.5">
                  {[asset.brand, asset.model].filter(Boolean).join(" · ")}
                </p>
              )}
            </div>

            <Row
              icon={Building2}
              label="Entity"
              value={
                asset.entity
                  ? `${asset.entity.name} (${asset.entity.code})`
                  : null
              }
            />
            <Row icon={Tag} label="Category" value={asset.category} />
            <Row icon={MapPin} label="Location" value={asset.location} />
            <Row
              icon={User}
              label="Current Holder"
              value={asset.holder || "Unassigned"}
            />
          </div>
        </div>

        {/* Warranty */}
        {asset.warrantyStatus && (
          <div>
            <WarrantyChip warrantyStatus={asset.warrantyStatus} />
          </div>
        )}

        {/* Primary photo */}
        {asset.primaryImageUrl && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <img
              src={asset.primaryImageUrl}
              alt={asset.name}
              className="w-full object-contain max-h-64 bg-slate-50"
            />
          </div>
        )}

        {/* Full detail link — logged-in GA/Admin/Finance/Executive only */}
        {canSeeFullDetail && (
          <Link
            to={`/assets/${id}`}
            className="flex items-center justify-between bg-white rounded-2xl border border-slate-200 px-4 py-4 shadow-sm hover:border-primary-300 hover:bg-primary-50/30 transition-colors group"
          >
            <div>
              <p className="text-sm font-semibold text-slate-800 group-hover:text-primary-700">
                View Full Detail
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                Documents, history, depreciation & more
              </p>
            </div>
            <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-primary-500 transition-colors" />
          </Link>
        )}

        {/* Sign in prompt — for unauthenticated users */}
        {!user && (
          <Link
            to={`/login?redirect=/assets/${id}/scan`}
            className="flex items-center justify-between bg-slate-100 rounded-2xl border border-slate-200 px-4 py-4 hover:bg-slate-200 transition-colors"
          >
            <p className="text-sm text-slate-600">Sign in for more options</p>
            <ArrowRight className="h-4 w-4 text-slate-400" />
          </Link>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 pb-4">
          Rhaya Inventory ·{" "}
          {new Date().toLocaleDateString("id-ID", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </p>
      </div>
    </div>
  );
}
